import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import { common, enums } from "@shared/constants";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import MapBanner from "@shared/modules/seating/components/MapBanner/MapBanner.web";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SEATING_ITEM_TYPE_MAP } from "@shared/modules/seating/types/seating-property-maps";
import { useBrowserTimezone } from "@shared/hooks/useBrowserTimezone";
import { AnalyticsService } from "@shared/services/analytics";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useEffect, useMemo } from "react";
import PublicAssignedSeating from "../PublicAssignedSeating/PublicAssignedSeating.web";
import { PublicAssignedSeatingModalSharedProps } from "./PublicAssignedSeatingModal.props";

export interface PublicAssignedSeatingContentProps {
  event?: PublicBaseEventSerializer;
  hideCheckoutButton?: boolean;
  hideDrawer?: boolean;
  venue: PublicVenueSerializer;
  onCheckout?: () => Promise<void> | void;
  isMembershipSeating?: boolean;
  membershipGroup?: PublicMembershipGroupSerializer;
  /**
   * When true, indicates the parent container uses fit-content height.
   * Triggers height constraints to prevent infinite growth with best available seating.
   */
  isFitContent?: boolean;
}

export const PublicAssignedSeatingContent = ({
  event,
  venue,
  hideCheckoutButton = false,
  hideDrawer = false,
  onCheckout,
  isMembershipSeating = false,
  membershipGroup,
  isFitContent = false,
}: PublicAssignedSeatingContentProps) => {
  const browserTimezone = useBrowserTimezone();
  const {
    seatingEventData,
    seatingItemArray,
    seatingServiceState,
    updateSeatingEventData,
    updateSeatingItemArray,
  } = useSeatingState();

  useEffect(() => {
    if (event && !isMembershipSeating) {
      AnalyticsService.eventViewed(event, {
        label: enums.INTERACTION_LABEL_MAP.SEATING_PAGE.id,
      });
    }
  }, [event, isMembershipSeating]);

  const { isFlagActive } = useDjangoWaffle();

  // Format the seating object based on whether it's for an event or membership
  const formattedSeatingObject = useMemo(() => {
    if (isMembershipSeating && membershipGroup) {
      const benefit = membershipGroup.reserved_seating_benefits?.[0];

      // Format membership group for seating
      return {
        ...seatingServiceState.createNewSeatingObject(
          membershipGroup,
          SEATING_ITEM_TYPE_MAP.MEMBERSHIP.propertyMap
        ),

        name:
          membershipGroup.current_season_on_sale?.name ?? membershipGroup?.name,
        starts_on: membershipGroup.current_season_on_sale?.start_date,
        ends_on: membershipGroup.current_season_on_sale?.end_date,
        timezone: browserTimezone,
        no_isolated_seats: benefit?.no_isolated_seats ?? false,
        assigned_space: benefit?.space ?? null,
      };
    }
    if (event) {
      // Format event for seating
      return seatingServiceState.createNewSeatingObject(
        event,
        SEATING_ITEM_TYPE_MAP.EVENT.propertyMap
      );
    }
    return undefined;
  }, [
    browserTimezone,
    event,
    membershipGroup,
    isMembershipSeating,
    seatingServiceState,
  ]);

  const formattedItems = useMemo(() => {
    if (isMembershipSeating && membershipGroup) {
      // Format membership levels for seating
      return seatingServiceState.createNewItemTypeArray(
        membershipGroup.membership_levels ?? [],
        SEATING_ITEM_TYPE_MAP.MEMBERSHIP.nestedItemType.propertyMap
      );
    }
    if (event) {
      // Format ticket types for seating (existing logic)
      const ticketArray = event.ticket_types || [];
      return seatingServiceState.createNewItemTypeArray(
        ticketArray,
        SEATING_ITEM_TYPE_MAP.EVENT.nestedItemType.propertyMap
      );
    }
    return undefined;
  }, [
    event?.id,
    membershipGroup?.id,
    isMembershipSeating,
    seatingServiceState,
  ]);

  const spaceData = useMemo(() => {
    if (isMembershipSeating && membershipGroup) {
      return {
        membership_benefit: membershipGroup.reserved_seating_benefits?.[0].id,
      };
    }
    if (event) {
      return { event: event.id };
    }
    return undefined;
  }, [event, membershipGroup, isMembershipSeating]);

  // Set the correct map item type
  useEffect(() => {
    seatingServiceState.mapItemType = isMembershipSeating
      ? common.SeatingMapItemType.MEMBERSHIP
      : common.SeatingMapItemType.EVENT;
  }, [isMembershipSeating, seatingServiceState]);

  // Load all values into the seating provider before the map is rendered
  useEffect(() => {
    // Update seatingEventData and seatingItemArray when necessary
    const loadInitialObject = formattedSeatingObject && !seatingEventData;
    const loadNewObject =
      formattedSeatingObject?.id !== seatingEventData?.id ||
      formattedSeatingObject?.assigned_space !==
        seatingEventData?.assigned_space;
    const updateObject = loadInitialObject || loadNewObject;

    const loadInitialItems = formattedItems && !seatingItemArray;
    const updateItems = loadInitialItems || loadNewObject;

    if (formattedSeatingObject && updateObject) {
      updateSeatingEventData(formattedSeatingObject);
    }
    if (formattedItems && updateItems) {
      updateSeatingItemArray(formattedItems);
    }
  }, [
    formattedSeatingObject,
    formattedItems,
    seatingEventData,
    seatingItemArray,
    updateSeatingEventData,
    updateSeatingItemArray,
  ]);

  const hasAssignedSeating = useMemo(() => {
    if (isMembershipSeating) {
      return !!(
        seatingEventData &&
        membershipGroup?.id === seatingEventData.id &&
        spaceData &&
        venue
      );
    }
    return !!(
      seatingEventData &&
      event?.id === seatingEventData.id &&
      spaceData &&
      venue
    );
  }, [
    event,
    membershipGroup,
    seatingEventData,
    spaceData,
    venue,
    isMembershipSeating,
  ]);

  if (!hasAssignedSeating) {
    return null;
  }

  return (
    <Flex
      id="public-assigned-seating-content"
      flex="1"
      flexDirection="column"
      minHeight="0"
    >
      {seatingEventData && (
        <MapBanner
          mapItemType={seatingServiceState.mapItemType}
          seatingObject={seatingEventData}
          venueObject={venue}
          isModal
        />
      )}
      <PublicAssignedSeating
        data-testid="assigned-seating-content"
        seatingObject={seatingEventData}
        spaceData={spaceData}
        venueData={venue}
        voucherData={{ userVouchers: [], basketVoucherCode: "" }}
        packageItemGroupParent={null}
        onCheckout={onCheckout}
        hideCheckoutButton={hideCheckoutButton}
        hideDrawer={hideDrawer}
        withBestAvailableSeating={isFlagActive(
          "enable_best_assigned_seating",
          venue.id
        )}
        isFitContent={isFitContent}
      />
    </Flex>
  );
};

export default function PublicAssignedSeatingModal({
  isOpen,
  onClose,
  eventId,
  venueId,
  membershipGroupId,
  ticketTypeFilter,
  showContinueButton = false,
}: PublicAssignedSeatingModalSharedProps) {
  const { t } = useTranslation("checkout");
  const { useRetrieve: useRetrieveVenue } = usePublicVenueService();
  const { handleBestAvailableSeatSelection } = useAssignedSeatingHelpers();

  const handleCheckout = () => {
    handleBestAvailableSeatSelection(onClose);
  };

  const { useRetrieveWithMembershipTicketTypesOverride } =
    usePublicEventService();
  const { data: eventData } = useRetrieveWithMembershipTicketTypesOverride(
    eventId,
    {
      options: {
        enabled: Boolean(eventId),
        select: (data) => ({
          ...data,
          ticket_types: ticketTypeFilter
            ? ticketTypeFilter(data?.ticket_types || [])
            : data?.ticket_types,
        }),
      },
    }
  );

  const { useRetrieve } = usePublicMembershipGroupsService();
  const { data: membershipGroupData } = useRetrieve(membershipGroupId, {
    options: {
      enabled: Boolean(membershipGroupId),
    },
  });

  const { data: venueData } = useRetrieveVenue(Number(venueId), {
    options: {
      enabled: Boolean(venueId),
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      data-testid="assigned-seating-modal"
    >
      <Modal.Header>
        <Text>
          {membershipGroupId ? membershipGroupData?.name : eventData?.name}
        </Text>
      </Modal.Header>
      <Modal.Body
        id="public-assigned-seating-modal-body"
        padding="none"
        display="flex"
        flexDirection="column"
      >
        <PublicAssignedSeatingContent
          event={eventData}
          venue={venueData}
          onCheckout={handleCheckout}
          isMembershipSeating={Boolean(membershipGroupId)}
          membershipGroup={membershipGroupData}
        />
      </Modal.Body>
      {showContinueButton && (
        <Modal.Footer
          display={{
            base: "flex",
            sm: "none", // On `sm` and larger, we still show the AS sidebar, which contains a direct Checkout button
          }}
        >
          <Button.Solid
            data-testid="checkout-label"
            onClick={handleCheckout}
            width="100%"
          >
            {t("Continue")}
          </Button.Solid>
        </Modal.Footer>
      )}
    </Modal>
  );
}
